import React, { useState, useEffect, useMemo } from 'react';
import { useCesium } from '../../../context/CesiumContext';
import { useMission } from '../../../context/MissionContext';
import type { OrbitStatusMeta, SatGroup, Satellite } from '../../../types/mission';
import styles from './StatusBar.module.scss';

const ORBIT_PROGRESS_SEGMENTS = 30;
const ORBIT_PROGRESS_HIDE_DELAY_MS = 3000;
const ORBIT_PROGRESS_EXIT_MS = 260;

interface StatusBarProps {
  accessWindowStatus?: 'ready' | 'computing' | 'failed' | null;
  accessWindowRunState?: any | null;
}

const StatusBar: React.FC<StatusBarProps> = ({ accessWindowStatus, accessWindowRunState }) => {
  const { cursorLatLon, measureDistanceKm, liveDrawingDistanceKm } = useCesium();
  const { state } = useMission();
  const [showOrbitProgress, setShowOrbitProgress] = useState(true);
  const [orbitProgressExiting, setOrbitProgressExiting] = useState(false);

  const lat = cursorLatLon ? cursorLatLon.lat.toFixed(4) : '—';
  const lon = cursorLatLon ? cursorLatLon.lon.toFixed(4) : '—';
  const orbitProgress = useMemo(() => {
    const isComplete = (status: OrbitStatusMeta | undefined, fallbackTotal?: number) => {
      if (!status || status.status !== 'completed') return false;
      const total = status.satellitesTotal ?? fallbackTotal;
      if (total == null) return true;
      return (status.satellitesDone ?? total) >= total;
    };

    let total = 0;
    let completed = 0;
    let failed = 0;
    let running = 0;
    let pending = 0;

    state.satellites.forEach((item) => {
      if ((item as SatGroup).type === 'group') {
        const group = item as SatGroup;
        const groupTotal = group.orbitPointStatus?.satellitesTotal ?? group.children.length;
        total += groupTotal;

        if (isComplete(group.orbitPointStatus, groupTotal)) {
          completed += groupTotal;
          return;
        }

        if (group.children.length === 0) {
          const groupStatus = group.orbitPointStatus?.status;
          if (groupStatus === 'failed') failed += groupTotal;
          else if (groupStatus === 'in_progress') running += groupTotal;
          else if (groupStatus === 'pending') pending += groupTotal;
          return;
        }

        group.children.forEach((child) => {
          const effectiveStatus = child.orbitPointStatus ?? group.orbitPointStatus;
          if (isComplete(effectiveStatus, 1)) {
            completed += 1;
          } else {
            const status = effectiveStatus?.status;
            if (status === 'failed') failed += 1;
            else if (status === 'in_progress') running += 1;
            else if (status === 'pending') pending += 1;
          }
        });
        return;
      }

      const sat = item as Satellite;
      total += 1;
      if (isComplete(sat.orbitPointStatus, 1)) {
        completed += 1;
      } else {
        const status = sat.orbitPointStatus?.status;
        if (status === 'failed') failed += 1;
        else if (status === 'in_progress') running += 1;
        else if (status === 'pending') pending += 1;
      }
    });

    if (total === 0) return null;
    const safeCompleted = Math.max(0, Math.min(completed, total));
    const label = failed > 0
      ? 'Orbital Details Failed'
      : safeCompleted >= total
        ? 'Orbital Details Ready'
        : running > 0 || pending > 0 || safeCompleted > 0
          ? 'Computing Orbital Details'
          : 'Waiting Orbital Details';

    const segmentCount = ORBIT_PROGRESS_SEGMENTS;
    const segmentsDone = total <= segmentCount
      ? safeCompleted
      : Math.round((safeCompleted / total) * segmentCount);

    return {
      label,
      completed: safeCompleted,
      total,
      failed,
      segmentCount,
      segmentsDone,
    };
  }, [state.satellites]);

  useEffect(() => {
    if (!orbitProgress) {
      setShowOrbitProgress(true);
      setOrbitProgressExiting(false);
      return;
    }

    const isComplete = orbitProgress.completed >= orbitProgress.total;
    if (!isComplete) {
      setShowOrbitProgress(true);
      setOrbitProgressExiting(false);
      return;
    }

    setShowOrbitProgress(true);
    setOrbitProgressExiting(false);
    const hideId = window.setTimeout(() => {
      setOrbitProgressExiting(true);
    }, ORBIT_PROGRESS_HIDE_DELAY_MS);
    const removeId = window.setTimeout(() => {
      setShowOrbitProgress(false);
      setOrbitProgressExiting(false);
    }, ORBIT_PROGRESS_HIDE_DELAY_MS + ORBIT_PROGRESS_EXIT_MS);

    return () => {
      window.clearTimeout(hideId);
      window.clearTimeout(removeId);
    };
  }, [orbitProgress]);


  const orbitProgressPercent = orbitProgress
    ? Math.min(100, (orbitProgress.completed / orbitProgress.total) * 100)
    : 0;
  const isOrbitComputing = orbitProgress && orbitProgress.completed < orbitProgress.total;
  const orbitProgressLabel = orbitProgress
    ? (() => {
      const percent = Math.round((orbitProgress.completed / orbitProgress.total) * 100);
      return `${orbitProgress.completed}/${orbitProgress.total}${orbitProgress.failed > 0 ? ` (${orbitProgress.failed} failed)` : ''} - ${percent}%`;
    })()
    : '';

  const accessWindowsProgressPercent = accessWindowRunState
    ? (((accessWindowRunState.pairsDone ?? 0) + (accessWindowRunState.pairsFailed ?? 0)) / Math.max(1, accessWindowRunState.pairsTotal)) * 100
    : 0;
  const isAccessWindowsComputing = accessWindowStatus === 'computing' && accessWindowsProgressPercent < 100;
  const accessWindowsProgressLabel = accessWindowRunState
    ? (() => {
      const done = (accessWindowRunState.pairsDone ?? 0) + (accessWindowRunState.pairsFailed ?? 0);
      const percent = Math.round((done / Math.max(1, accessWindowRunState.pairsTotal)) * 100);
      return `${done}/${accessWindowRunState.pairsTotal}${accessWindowRunState.pairsFailed > 0 ? ` (${accessWindowRunState.pairsFailed} failed)` : ''} - ${percent}%`;
    })()
    : '';

  return (
    <div className={styles.bar}>
      <div className={styles.progressArea}>
        {isAccessWindowsComputing && (
          <div className={styles.orbitProgressBarContainer}>
            <div className={styles.orbitProgressBarLeft}>
              <span className={styles.orbitProgressBarLabel}>Access Windows</span>
              <span className={styles.orbitProgressBarValue}>{accessWindowsProgressLabel}</span>
            </div>
            <div className={styles.orbitProgressTrack}>
              <div className={styles.orbitProgressFill} style={{ width: `${accessWindowsProgressPercent}%` }} />
            </div>
          </div>
        )}
        {isOrbitComputing && (
          <div className={styles.orbitProgressBarContainer}>
            <div className={styles.orbitProgressBarLeft}>
              <span className={styles.orbitProgressBarLabel}>Orbital Details</span>
              <span className={styles.orbitProgressBarValue}>{orbitProgressLabel}</span>
            </div>
            <div className={styles.orbitProgressTrack}>
              <div className={styles.orbitProgressFill} style={{ width: `${orbitProgressPercent}%` }} />
            </div>
          </div>
        )}
      </div>
      <div className={styles.rightTools}>
        <span className={styles.coords}>
          Lat <span className={styles.val}>{lat}°</span>
          <span className={styles.sep}>·</span>
          Long <span className={styles.val}>{lon}°</span>
          {measureDistanceKm !== null && (
            <>
              <span className={styles.sep}>·</span>
              Dist <span className={styles.val}>{measureDistanceKm.toFixed(1)} km</span>
            </>
          )}
          {liveDrawingDistanceKm !== null && (
            <>
              <span className={styles.sep}>·</span>
              Edge <span className={styles.val}>{liveDrawingDistanceKm < 1 ? `${(liveDrawingDistanceKm * 1000).toFixed(0)} m` : `${liveDrawingDistanceKm.toFixed(2)} km`}</span>
            </>
          )}
        </span>
      </div>
    </div>
  );
};

export default StatusBar;
